import { Crosshair, Globe, Info } from 'lucide-react';
import React, { useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useWizardFooterConfig, WizardModalFooter } from '@/components/wizards';
import { cn } from '@/lib/utils';
import { HuntScopeStep } from '@/sections/insights/aiAssistant/components/HuntScopeStep';
import { formatLocalDateTimeInput } from '@/sections/insights/hunts/dateTimeLocal';
import { DEFAULT_AGENT_NAME } from '@/sections/insights/hunts/huntForm';

export const DEFAULT_MANDATE = `Autonomously assess the in-scope environment for exploitable paths that could lead to unauthorized access, privilege escalation, or sensitive data exposure. Focus on remote code execution, authentication weaknesses, default credentials, authentication and authorization bypasses, misconfigurations leading to compromise, exposed secrets, injection attacks, and privilege-escalation validation. The focus should be on exploitability of high to critical risks. Customers require demonstration and proof to engage remediation teams.`;

export const DEFAULT_GUARDRAILS = `Operate only within authorized scope. Do not perform denial-of-service, stress testing, brute force, password spraying, credential stuffing, phishing, social engineering, malware deployment, persistence, destructive actions, or bulk data exfiltration. Do not modify production data, change credentials, create accounts, alter configurations, restart services, disable security controls, clear logs, or evade monitoring. Use low-rate, low-concurrency testing. Stop or back off if testing causes errors, instability, lockout risk, throttling, or service degradation. If sensitive data is encountered, collect only minimal metadata or redacted evidence needed to prove exposure. When following redirects, stop before sending active probes, credentials, payloads, or exploit attempts to assets outside authorized scope. Passive observation of the redirect chain is permitted.`;

export const DEFAULT_FINISH_CRITERIA = `End the hunt early if any of the following occur:

1. A material critical compromise path has been validated.
2. Three consecutive iterations produce no new high-confidence findings.
3. If the agent encounters signs of service instability, account lockout risk, rate limiting, or operational impact, move on to another target.
4. If further progress would require brute force, phishing, persistence, or destructive testing, move on to another target.
5. Additional validation would materially increase risk without improving confidence.
6. The allotted duration expires.`;

interface LaunchHannibalDialogProps {
  open: boolean;
  mission: string;
  onLaunch: (config: {
    mandate: string;
    guardrails: string;
    finishCriteria: string;
    expiresAt: string;
    agentName: string;
    scope: string[];
  }) => void;
  onCancel: () => void;
  isPending: boolean;
  huntAgents?: Array<{ name: string; title?: string }>;
}

type StepKey = 'type' | 'scope' | 'mandate' | 'finish';

const STEP_TITLES: Record<StepKey, string> = {
  type: 'Infrastructure scope',
  scope: 'Select assets',
  mandate: 'Mandate & guardrails',
  finish: 'Finish criteria',
};

const SCOPE_MODES: ReadonlyArray<{
  value: 'all' | 'specific';
  label: string;
  sublabel: string;
  icon: typeof Globe;
}> = [
  {
    value: 'all',
    label: 'All infrastructure',
    sublabel: 'Hannibal hunts across every active target in Guard.',
    icon: Globe,
  },
  {
    value: 'specific',
    label: 'Specific infrastructure',
    sublabel: 'Choose the exact assets to scope the hunt to.',
    icon: Crosshair,
  },
];

function FieldLabel({
  htmlFor,
  label,
  tip,
  children,
}: {
  htmlFor: string;
  label: string;
  tip: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={`About ${label}`}
            className="text-chariot-text-secondary hover:text-chariot-text-primary focus-visible:text-chariot-text-primary focus-visible:ring-chariot-brand-primary inline-flex size-5 items-center justify-center rounded-xs outline-hidden focus-visible:ring-2"
          >
            <Info className="size-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="right" className="z-[200] max-w-xs">
          {tip}
        </TooltipContent>
      </Tooltip>
      {children}
    </div>
  );
}

function LaunchHannibalForm({
  mission,
  onLaunch,
  onCancel,
  isPending,
  huntAgents,
}: Omit<LaunchHannibalDialogProps, 'open'>) {
  const [scopeMode, setScopeMode] = useState<'all' | 'specific'>('all');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [mandate, setMandate] = useState(mission);
  const [guardrails, setGuardrails] = useState('');
  const [finishCriteria, setFinishCriteria] = useState('');
  const [durationHours, setDurationHours] = useState(24);
  const [agentName, setAgentName] = useState(DEFAULT_AGENT_NAME);
  const [scope, setScope] = useState<string[]>([]);

  // INVARIANT: 'scope' is never the terminal step, so a false canGoNext never
  // disables the Launch button.
  const steps: StepKey[] =
    scopeMode === 'specific'
      ? ['type', 'scope', 'mandate', 'finish']
      : ['type', 'mandate', 'finish'];
  const currentStep = steps[currentIndex];

  // Switching scope mode is the ONLY thing that reshapes `steps`. Re-anchor to
  // step 0 so `currentIndex` can never point past/into the wrong slot of the
  // rebuilt array (H1), and drop any specific-branch selection when going back
  // to 'all' so `scope` stays a true function of `scopeMode` (H2). The dialog
  // also resets fully on open via key={String(open)}; this handles in-session
  // toggles. Plain function — React Compiler is on (no useCallback).
  const handleScopeModeChange = (mode: 'all' | 'specific') => {
    setScopeMode(mode);
    setCurrentIndex(0);
    if (mode === 'all') {
      setScope([]);
    }
  };

  const canLaunch = !isPending;

  const handleLaunch = () => {
    if (!canLaunch) return;
    const expiresAtDate = new Date();
    expiresAtDate.setHours(expiresAtDate.getHours() + durationHours);
    const expiresAt = formatLocalDateTimeInput(expiresAtDate);
    onLaunch({
      mandate: mandate.trim() || DEFAULT_MANDATE,
      guardrails: guardrails.trim() || DEFAULT_GUARDRAILS,
      finishCriteria: finishCriteria.trim() || DEFAULT_FINISH_CRITERIA,
      expiresAt,
      agentName,
      scope: scopeMode === 'specific' ? scope : [],
    });
  };

  const footerConfig = useWizardFooterConfig({
    canGoNext: currentStep !== 'scope' || scope.length > 0,
    canGoBack: currentIndex > 0,
    isPending,
    isLastStep: currentIndex === steps.length - 1,
    onNext: () => setCurrentIndex(i => Math.min(i + 1, steps.length - 1)),
    onBack: () => setCurrentIndex(i => Math.max(i - 1, 0)),
    onCancel,
    onSubmit: handleLaunch,
    labels: { submit: 'Launch Hannibal', submitting: 'Launching...' },
  });

  const showAgentSelector = huntAgents !== undefined && huntAgents.length > 1;

  return (
    <TooltipProvider>
      <DialogHeader>
        <DialogTitle>Launch Hannibal</DialogTitle>
        <DialogDescription>
          {currentStep === 'type'
            ? 'Choose whether Hannibal hunts all infrastructure or specific assets.'
            : currentStep === 'scope'
              ? 'Select the assets to scope this hunt to.'
              : currentStep === 'mandate'
                ? 'Define the hunt objective and operational boundaries.'
                : 'Set exit criteria and hunt duration.'}
        </DialogDescription>
      </DialogHeader>

      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1.5" aria-hidden="true">
          {steps.map((s, i) => (
            <span
              key={s}
              className={cn(
                'size-1.5 rounded-full transition-colors',
                i <= currentIndex
                  ? 'bg-chariot-brand-primary'
                  : 'bg-chariot-stroke'
              )}
            />
          ))}
        </div>
        <span className="text-chariot-text-secondary text-xs">
          {STEP_TITLES[currentStep]}
        </span>
        <span className="sr-only" aria-live="polite">
          Step {currentIndex + 1} of {steps.length}: {STEP_TITLES[currentStep]}
        </span>
      </div>

      {/* Step bodies share a min-h-[460px] floor for visual stability; the mandate/guardrails/finish textareas are sized via rows to show their full default text without an internal scrollbar at 100% browser zoom. */}
      {currentStep === 'type' && (
        <div className="flex min-h-[460px] flex-col justify-center gap-6">
          <div className="flex flex-col gap-1.5 text-center">
            <h3 className="text-chariot-text-primary text-base font-semibold">
              What should Hannibal hunt?
            </h3>
            <p className="text-chariot-text-secondary text-sm">
              Choose the scope for this hunt. You can narrow to specific assets
              next.
            </p>
          </div>
          <div
            role="radiogroup"
            aria-label="Hunt infrastructure scope"
            className="mx-auto flex w-full max-w-md flex-col gap-3"
          >
            {SCOPE_MODES.map(opt => {
              const isSelected = scopeMode === opt.value;
              const Icon = opt.icon;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  onClick={() => handleScopeModeChange(opt.value)}
                  data-testid={`hunt-scope-mode-${opt.value}`}
                  className={cn(
                    'flex items-start gap-3 rounded-lg border p-4 text-left transition-colors',
                    'focus-visible:ring-chariot-brand-primary outline-hidden focus-visible:ring-2',
                    isSelected
                      ? 'border-chariot-brand-primary bg-chariot-background-secondary'
                      : 'border-chariot-stroke hover:bg-chariot-background-secondary'
                  )}
                >
                  <Icon
                    className={cn(
                      'mt-0.5 size-5 shrink-0',
                      isSelected
                        ? 'text-chariot-brand-primary'
                        : 'text-chariot-text-secondary'
                    )}
                  />
                  <span className="flex flex-col gap-0.5">
                    <span className="text-chariot-text-primary text-sm font-medium">
                      {opt.label}
                    </span>
                    <span className="text-chariot-text-secondary text-xs">
                      {opt.sublabel}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
          <p className="text-chariot-text-tertiary mx-auto max-w-md text-center text-xs">
            All active targets is the default and matches the prior behaviour.
          </p>
        </div>
      )}

      {currentStep === 'scope' && (
        <div className="flex min-h-[460px] flex-col gap-4">
          <HuntScopeStep value={scope} onChange={setScope} />
        </div>
      )}

      {currentStep === 'mandate' && (
        <div className="flex min-h-[460px] flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <FieldLabel
              htmlFor="mandate"
              label="Hunt Mandate"
              tip="The hunt agent's primary objective. Defaults to exploitable high-to-critical risks."
            >
              {mission && (
                <span className="text-success ml-auto flex items-center gap-1 text-[11px] font-medium">
                  <span className="bg-success inline-block size-1.5 rounded-full" />
                  Pre-filled from conversation
                </span>
              )}
            </FieldLabel>
            <Textarea
              id="mandate"
              rows={7}
              value={mandate}
              onChange={e => setMandate(e.target.value)}
              placeholder={DEFAULT_MANDATE}
            />
            {!mandate.trim() && (
              <button
                type="button"
                onClick={() => setMandate(DEFAULT_MANDATE)}
                className="text-chariot-brand-primary text-left text-xs hover:underline"
              >
                Use default prompt
              </button>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <FieldLabel
              htmlFor="guardrails"
              label="Guardrails"
              tip="Operational boundaries, checked per tool call by a separate LLM judge."
            />
            <Textarea
              id="guardrails"
              rows={11}
              value={guardrails}
              onChange={e => setGuardrails(e.target.value)}
              placeholder={DEFAULT_GUARDRAILS}
            />
            {!guardrails.trim() && (
              <button
                type="button"
                onClick={() => setGuardrails(DEFAULT_GUARDRAILS)}
                className="text-chariot-brand-primary text-left text-xs hover:underline"
              >
                Use default guardrails
              </button>
            )}
          </div>
        </div>
      )}

      {currentStep === 'finish' && (
        <div className="flex min-h-[460px] flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <FieldLabel
              htmlFor="finishCriteria"
              label="Finish Criteria"
              tip="Conditions that let the agent end the hunt early."
            />
            <Textarea
              id="finishCriteria"
              rows={10}
              value={finishCriteria}
              onChange={e => setFinishCriteria(e.target.value)}
              placeholder={DEFAULT_FINISH_CRITERIA}
            />
            {!finishCriteria.trim() && (
              <button
                type="button"
                onClick={() => setFinishCriteria(DEFAULT_FINISH_CRITERIA)}
                className="text-chariot-brand-primary text-left text-xs hover:underline"
              >
                Use default criteria
              </button>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Duration</Label>
            <div className="border-chariot-stroke flex overflow-hidden rounded-lg border">
              {[8, 24, 48, 72].map(hours => (
                <button
                  key={hours}
                  type="button"
                  onClick={() => setDurationHours(hours)}
                  className={cn(
                    'border-chariot-stroke flex flex-1 flex-col items-center justify-center gap-0.5 border-r py-3 transition-colors last:border-r-0',
                    durationHours === hours
                      ? 'bg-chariot-brand-primary text-white'
                      : 'bg-chariot-background-secondary text-chariot-text-secondary hover:bg-chariot-background-secondary/80'
                  )}
                >
                  <span className="text-base leading-none font-semibold">
                    {hours}
                  </span>
                  <span className="text-[10px] tracking-wider uppercase opacity-75">
                    hours
                  </span>
                </button>
              ))}
            </div>
          </div>

          {showAgentSelector && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="agentName">Agent</Label>
              <Select value={agentName} onValueChange={setAgentName}>
                <SelectTrigger id="agentName">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {huntAgents.map(agent => (
                    <SelectItem key={agent.name} value={agent.name}>
                      {agent.title ?? agent.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      )}

      <WizardModalFooter config={footerConfig} />
    </TooltipProvider>
  );
}

export function LaunchHannibalDialog({
  open,
  mission,
  onLaunch,
  onCancel,
  isPending,
  huntAgents,
}: LaunchHannibalDialogProps) {
  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onCancel()}>
      <DialogContent className="max-w-2xl">
        <LaunchHannibalForm
          key={String(open)}
          mission={mission}
          onLaunch={onLaunch}
          onCancel={onCancel}
          isPending={isPending}
          huntAgents={huntAgents}
        />
      </DialogContent>
    </Dialog>
  );
}
